/**
 * Time and number formatting.
 *
 * The relative-time helper exists because `requestScan` is allowed to hand back
 * a scan that was already running or has recently finished for the same target.
 * The contract points at `startedAt` and `finishedAt` for exactly this: a client
 * that needs to say "scanned 40 seconds ago" should read them rather than assume
 * the scan began with this request. Somebody who asks for a scan and gets a
 * three-minute-old answer deserves to be told, not to guess.
 */

const RELATIVE = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })

const UNITS: readonly [Intl.RelativeTimeFormatUnit, number][] = [
  ['second', 1000],
  ['minute', 60_000],
  ['hour', 3_600_000],
  ['day', 86_400_000],
]

export function relativeTime(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return ''
  const then = Date.parse(iso)
  if (Number.isNaN(then)) return ''

  const deltaMs = then - now
  const absMs = Math.abs(deltaMs)

  if (absMs < 5_000) return 'just now'

  let unit: Intl.RelativeTimeFormatUnit = 'second'
  let size = 1000
  for (const [candidateUnit, candidateSize] of UNITS) {
    if (absMs >= candidateSize) {
      unit = candidateUnit
      size = candidateSize
    }
  }
  return RELATIVE.format(Math.round(deltaMs / size), unit)
}

/** Absolute timestamp for a `title` attribute, so hovering gives the real thing. */
export function absoluteTime(iso: string | null | undefined): string {
  if (!iso) return ''
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return ''
  return parsed.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'medium' })
}

export function plural(count: number, singular: string, pluralForm: string): string {
  return `${count} ${count === 1 ? singular : pluralForm}`
}

/** Elapsed wall time between two instants, as "12s" or "1m 04s". */
export function duration(startIso: string | null | undefined, endIso: string | null | undefined): string {
  if (!startIso || !endIso) return ''
  const ms = Date.parse(endIso) - Date.parse(startIso)
  if (Number.isNaN(ms) || ms < 0) return ''
  const totalSeconds = Math.round(ms / 1000)
  if (totalSeconds < 60) return `${totalSeconds}s`
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}m ${String(seconds).padStart(2, '0')}s`
}
